/* HPF per-product storefront quantity controls. Not a server-side checkout rule. */
(() => {
  'use strict';
  const config = window.hpfQuantityCapConfig;
  if (!config || window.hpfQuantityCap) return;
  const context = window.hpfQuantityCapContext || config;
  const variantProducts = new Map(), productIds = new Set(), limit = config.maximum;
  for (const product of config.products) {
    productIds.add(String(product.id));
    for (const variant of product.variants) variantProducts.set(String(variant), String(product.id));
  }
  const ids = new Set(variantProducts.keys());
  const originalFetch = window.fetch.bind(window);
  const selector = 'quantity-input input[type="number"],input.quantity__input[type="number"],input[name="quantity"][type="number"]';
  let cart = Array.isArray(context.initialCart?.items) ? context.initialCart : { items: [] };
  let queue = Promise.resolve(), scheduled = false, verified = false;
  const approvedCheckout = new WeakSet();
  const bases = new WeakMap();
  const productOf = item => variantProducts.get(String(item.variant_id || item.id)) || (productIds.has(String(item.product_id)) ? String(item.product_id) : null);
  const target = item => productOf(item) !== null;
  const total = (value, productId) => value.items.reduce((sum, item) => sum + (target(item) && (!productId || productOf(item) === productId) ? Number(item.quantity) : 0), 0);
  const counts = value => {
    const result = new Map();
    for (const item of value.items) { const id = productOf(item); if (id) result.set(id, (result.get(id) || 0) + Number(item.quantity)); }
    return result;
  };
  const over = value => [...counts(value).values()].some(quantity => quantity > limit);
  const root = () => window.Shopify?.routes?.root || '/';
  const positive = (value, fallback) => Number.isFinite(Number(value)) && value !== '' && value !== null ? Number(value) : fallback;
  const set = (element, name, value) => { if (element.getAttribute(name) !== String(value)) element.setAttribute(name, String(value)); };
  function identify(input) {
    const variant = input.dataset.quantityVariantId;
    if (variant) {
      if (!ids.has(variant)) return null;
      const indexed = cart.items[Number(input.dataset.index) - 1];
      return { cart: true, productId: variantProducts.get(variant), item: indexed && String(indexed.variant_id) === variant ? indexed : cart.items.find(x => String(x.variant_id) === variant) };
    }
    const form = input.form || input.closest('product-form')?.querySelector('form');
    const id = form?.querySelector('[name="id"]')?.value;
    const productId = variantProducts.get(String(id)) || (!id && productIds.has(String(context.productPageId)) && input.closest('[id^="MainProduct-"]') ? String(context.productPageId) : null);
    if (productId) return { cart: false, productId };
    return null;
  }
  function enhance(input) {
    const kind = identify(input);
    if (!kind) return;
    if (!input.readOnly) input.readOnly = true;
    set(input, 'aria-readonly', 'true'); set(input, 'inputmode', 'none'); set(input, 'data-hpf-cap', 'true');
    let base = bases.get(input), current = input.getAttribute('max');
    if (!base || current !== base.applied) base = { maximum: positive(current, Infinity), applied: null };
    const minimum = Math.max(1, positive(input.getAttribute('min'), 1));
    const productTotal = total(cart, kind.productId);
    const others = kind.cart ? productTotal - Number(kind.item?.quantity || 0) : productTotal;
    const maximum = Math.min(base.maximum, Math.max(minimum, limit - others));
    set(input, 'min', minimum); set(input, 'max', maximum); base.applied = String(maximum); bases.set(input, base);
    const value = Number(input.value), step = Math.max(1, positive(input.getAttribute('step'), 1));
    const wrapper = input.closest('quantity-input') || input.parentElement;
    for (const [name, disabled] of [['plus', value + step > maximum || (!kind.cart && productTotal >= limit)], ['minus', value - step < minimum]]) {
      const button = wrapper.querySelector('button[name="' + name + '"]');
      if (button) { if (button.disabled !== disabled) button.disabled = disabled; button.classList.toggle('disabled', disabled); }
    }
  }
  function apply() { scheduled = false; document.querySelectorAll(selector).forEach(enhance); }
  function schedule() { if (!scheduled) { scheduled = true; queueMicrotask(apply); } }
  async function refresh() {
    verified = false;
    const response = await originalFetch(root() + 'cart.js?hpf_cap=' + Date.now(), { cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) throw new Error('Cart verification unavailable');
    const value = await response.json(); if (!Array.isArray(value.items)) throw new Error('Invalid cart response');
    cart = value; verified = true; schedule(); return value;
  }
  function notify(anchor) {
    let node = document.querySelector('[data-hpf-cap-message]');
    if (!node) {
      node = document.createElement('p'); node.dataset.hpfCapMessage = 'true'; node.setAttribute('role', 'alert');
      node.style.cssText = 'color:#9b2222;font-size:14px;line-height:1.5;margin:12px 0';
    }
    const visible = element => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden';
    const places = [...document.querySelectorAll('.drawer__footer,.cart__footer,.cart__blocks,.product-form__buttons')];
    const place = anchor?.closest('.drawer__footer,.cart__footer,.cart__blocks,.product-form__buttons') || anchor?.parentElement || places.find(visible) || document.body;
    if (node.parentElement !== place) place.append(node);
    if (node.textContent !== config.message) node.textContent = config.message;
  }
  function blocked(status = 422) {
    notify(); schedule();
    const text = status === 422 ? config.message : config.unavailableMessage;
    return new Response(JSON.stringify({ status, message: text, description: text, errors: text }), { status, headers: { 'Content-Type': 'application/json' } });
  }
  async function decode(input, init) {
    let body = init?.body;
    if (body === undefined && input instanceof Request) body = await input.clone().text();
    if (typeof body === 'string') { try { return JSON.parse(body); } catch (_) { body = new URLSearchParams(body); } }
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const data = {};
      for (const [key, value] of body.entries()) {
        let match = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
        if (match) { data.items ||= []; data.items[Number(match[1])] ||= {}; data.items[Number(match[1])][match[2]] = value; }
        else if ((match = key.match(/^updates\[([^\]]*)\]$/))) {
          if (!match[1]) { data.updates ||= []; data.updates.push(value); }
          else { data.updates ||= {}; data.updates[match[1]] = value; }
        } else data[key] = value;
      }
      return data;
    }
    return body || {};
  }
  function projected(operation, data) {
    const result = counts(cart);
    const adjust = (item, quantity) => { const id = productOf(item); if (id) result.set(id, (result.get(id) || 0) + quantity); };
    if (operation === 'clear') return new Map();
    if (operation === 'add') {
      for (const item of data.items || [data]) adjust(item, positive(item.quantity, 1));
      return result;
    }
    if (operation === 'change') {
      const item = data.line ? cart.items[Number(data.line) - 1] : cart.items.find(x => String(x.key) === String(data.id) || String(x.variant_id) === String(data.id));
      if (item && target(item)) adjust(item, positive(data.quantity, item.quantity) - Number(item.quantity));
      return result;
    }
    const updates = data.updates || {}, used = new Set();
    cart.items.forEach((item, index) => {
      let value;
      if (Array.isArray(updates)) value = updates[index];
      else if (Object.hasOwn(updates, item.key)) value = updates[item.key];
      else if (!used.has(String(item.variant_id)) && Object.hasOwn(updates, String(item.variant_id))) value = updates[String(item.variant_id)];
      if (value !== undefined) { used.add(String(item.variant_id)); if (target(item)) adjust(item, positive(value, item.quantity) - Number(item.quantity)); }
    });
    if (!Array.isArray(updates)) for (const [key, value] of Object.entries(updates)) if (ids.has(key) && !cart.items.some(x => String(x.variant_id) === key)) adjust({ id: key }, positive(value, 0));
    return result;
  }
  window.fetch = function(input, init) {
    const url = new URL(input instanceof Request ? input.url : String(input), location.href);
    const operation = url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?\/?$/)?.[1];
    const method = init?.method || (input instanceof Request ? input.method : 'GET');
    if (url.origin !== location.origin || !operation || method.toUpperCase() !== 'POST') return originalFetch(input, init);
    const task = queue.then(async () => {
      const data = await decode(input, init);
      if (operation === 'add' && !(data.items || [data]).some(target)) return originalFetch(input, init);
      if (operation !== 'clear') {
        try { await refresh(); } catch (_) { return blocked(503); }
        const before = counts(cart), after = projected(operation, data);
        if ([...after].some(([id, quantity]) => quantity > limit && quantity > (before.get(id) || 0))) return blocked();
      }
      const response = await originalFetch(input, init);
      if (response.ok) {
        let value; try { value = await response.clone().json(); } catch (_) {}
        if (value && Array.isArray(value.items) && value.item_count !== undefined) { cart = value; verified = true; schedule(); }
        else { try { await refresh(); } catch (_) {} }
      }
      return response;
    });
    queue = task.catch(() => {}); return task;
  };
  document.addEventListener('change', e => { if (e.target.matches?.(selector)) schedule(); }, true);
  for (const name of ['beforeinput', 'paste', 'drop']) document.addEventListener(name, e => { if (e.target.matches?.(selector) && identify(e.target)) e.preventDefault(); }, true);
  document.addEventListener('keydown', e => { if (e.target.matches?.(selector) && identify(e.target) && ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown'].includes(e.key)) e.preventDefault(); }, true);
  function guardCheckout(event, element, resume) {
    if (approvedCheckout.has(element)) { approvedCheckout.delete(element); return; }
    const evidence = total(cart) > 0 || [...document.querySelectorAll('input[data-quantity-variant-id]')].some(input => ids.has(input.dataset.quantityVariantId));
    if (!over(cart) && (verified || !evidence)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (over(cart)) { notify(element); return; }
    refresh().then(() => {
      if (over(cart)) notify(element);
      else { approvedCheckout.add(element); resume(); }
    }).catch(() => notify(element));
  }
  document.addEventListener('click', e => {
    const button = e.target.closest?.('quantity-input button[name="plus"],quantity-input button[name="minus"]');
    if (button) { const input = button.closest('quantity-input').querySelector(selector); if (input && identify(input)) { enhance(input); if (button.disabled) { e.preventDefault(); e.stopImmediatePropagation(); } } }
    const checkout = e.target.closest?.('a[href*="/checkout"],button[name="checkout"],.cart__checkout-button,button[onclick*="gotoShopifyCheckout"],button[data-skip-cart-button="true"],#cart-page-pp-btn,[onclick*="itcCartPagePayPal"]');
    if (checkout) guardCheckout(e, checkout, () => checkout.click());
  }, true);
  document.addEventListener('submit', e => {
    if (e.submitter?.name === 'checkout' || e.submitter?.matches('.cart__checkout-button,[onclick*="gotoShopifyCheckout"]') || /\/checkout/.test(e.target.action || '')) {
      const form = e.target, submitter = e.submitter;
      guardCheckout(e, form, () => form.requestSubmit(submitter));
    }
  }, true);
  const start = () => {
    apply();
    new MutationObserver(schedule).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['min', 'max', 'value', 'readonly', 'data-quantity-variant-id'] });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
  window.hpfQuantityCap = { refresh, version: '20261103-hpf-cod-v1', ready: refresh().catch(() => null) };
})();
